/**
 * BookReadingView - Shared reading experience component
 * 
 * SINGLE SOURCE OF TRUTH for book reading UI.
 * Used by:
 * - Parent library reader (app/library/book/[id].tsx)
 * - Content editor preview (components/content-editor/PreviewModal.tsx)
 * 
 * Modes:
 * - 'illustrated' (default): Turn Art background with text overlay at bottom
 * - 'text-only': Full-screen book paper background, text fills the page,
 *   auto-paginates when text overflows at current font size
 * 
 * @see CURSOR_HANDOFF.md for architecture notes
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Wand2 } from 'lucide-react';
import { TurnColorSentence, TurnColorMapping, STANDARD_COLORS, type TurnCodeError } from '@/lib/turn-color';
import { paginateWords } from './paginateWords';

// ============================================================================
// Types
// ============================================================================

export interface ReadingPage {
  /** Page text content */
  text: string;
  /** Turn Art illustration URL (null = gradient fallback) */
  turnArtUrl: string | null;
  /** Words with Turn Color codes for rendering */
  words: Array<{
    word: string;
    turnCode: string | null;
    inLexicon: boolean;
    /** Skill ID from turn_color_words (e.g., "2-a-black") */
    skillId?: string;
  }>;
}

export interface BookReadingViewProps {
  /** Array of pages to display */
  pages: ReadingPage[];
  /** Current page index (0-based) */
  currentPageIndex: number;
  /** Callback when page changes */
  onPageChange: (index: number) => void;
  /** Turn Color mappings (defaults to STANDARD_COLORS) */
  colorMappings?: TurnColorMapping[];
  /** Turn Color phase: 1 = squares, 2 = underlines (default: 2) */
  phase?: 1 | 2;
  /** Font size in pixels (default: 28) */
  fontSize?: number;
  /** Line spacing multiplier (default: 1.8) */
  lineSpacing?: number;
  /** Whether to show navigation controls (default: true) */
  showNavigation?: boolean;
  /** Whether to show progress bar (default: true) */
  showProgress?: boolean;
  /** Whether to enable keyboard navigation (default: true) */
  enableKeyboard?: boolean;
  /** Whether to enable touch/swipe navigation (default: true) */
  enableTouch?: boolean;
  /** Optional className for container */
  className?: string;
  /** Container height (default: '100%') */
  height?: string;
  /** Theme: 'dark' = scan agent (global design), 'light' = library reader (default) */
  theme?: 'dark' | 'light';
  /** Show animated placeholder when Turn Art is generating (default: false) */
  showTurnArtPlaceholder?: boolean;
  /** Callback when a Turn Code validation error occurs - allows parent to handle/fix */
  onTurnCodeError?: (error: TurnCodeError) => void;
  /**
   * Skill-based word filter. When provided (as an array of skill_ids), only
   * words whose level matches one of the active skills will show Turn Color
   * highlighting. Other words render as plain text.
   * Format: skill_id[] from child_skill_progress (e.g., ["1-a-red", "2-a-black"])
   */
  skillFilter?: string[] | null;
  /**
   * Display mode:
   * - 'illustrated': Turn Art background with text overlay (default, backward compat)
   * - 'text-only': Full-screen book paper background, text fills page, auto-paginated
   */
  mode?: 'illustrated' | 'text-only';
}

// ============================================================================
// Auto-pagination hook for text-only mode
// ============================================================================

/**
 * Given all logical pages, flatten their words and split into virtual pages
 * that fit within the given container height at the given font size.
 * 
 * Uses a hidden measurement div to determine how many words fit per page.
 */
function useAutoPagination(
  pages: ReadingPage[],
  fontSize: number,
  lineSpacing: number,
  container: HTMLDivElement | null,
  mode: 'illustrated' | 'text-only',
  navHeight: number = 0,
) {
  // Initialize to `pages` (not []) so the component renders the DOM on first
  // mount. This ensures the ref callback fires and container becomes non-null,
  // which in turn lets the ResizeObserver effect attach.
  const [virtualPages, setVirtualPages] = useState<ReadingPage[]>(pages);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only run auto-pagination in text-only mode
  const isTextOnly = mode === 'text-only';

  const paginate = useCallback(() => {
    if (!isTextOnly) {
      setVirtualPages(pages);
      return;
    }

    if (!container || pages.length === 0) {
      setVirtualPages(pages);
      return;
    }

    const containerHeight = container.clientHeight;
    const verticalPadding = 96;
    const availableHeight = containerHeight - verticalPadding - navHeight;

    if (availableHeight <= 0) {
      setVirtualPages(pages);
      return;
    }

    // Create a hidden measurement element for DOM-based height calculation
    const measurer = document.createElement('div');
    measurer.className = 'turn-color-text';
    measurer.style.position = 'absolute';
    measurer.style.visibility = 'hidden';
    // Match the actual text container width: full container minus horizontal padding (px-8 = 32px each side, sm:px-16 = 64px each side)
    const horizontalPadding = window.innerWidth >= 640 ? 128 : 64;
    measurer.style.width = `${container.clientWidth - horizontalPadding}px`;
    measurer.style.fontSize = `${fontSize}px`;
    measurer.style.lineHeight = String(lineSpacing);
    measurer.style.wordBreak = 'break-word';
    measurer.style.whiteSpace = 'pre-wrap';
    document.body.appendChild(measurer);

    // Delegate to the extracted pure algorithm with a DOM measurement callback
    const measureHeight = (text: string): number => {
      measurer.textContent = text;
      return measurer.scrollHeight;
    };

    const result = paginateWords(pages, availableHeight, measureHeight);

    document.body.removeChild(measurer);
    setVirtualPages(result);
  }, [pages, fontSize, lineSpacing, isTextOnly, container, navHeight]);

  // Debounced repagination on font size or page changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(paginate, 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [paginate]);

  // Repaginate on container resize. Because `container` is a state variable
  // (not a ref), this effect re-runs when the element mounts/unmounts,
  // fixing the original bug where ResizeObserver never attached.
  useEffect(() => {
    if (!isTextOnly || !container) return;

    const observer = new ResizeObserver(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(paginate, 150);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isTextOnly, paginate, container]);

  return virtualPages;
}

// ============================================================================
// Component
// ============================================================================

export function BookReadingView({
  pages,
  currentPageIndex,
  onPageChange,
  colorMappings = STANDARD_COLORS,
  phase = 2,
  fontSize = 28,
  lineSpacing = 1.4,
  showNavigation = true,
  showProgress = true,
  enableKeyboard = true,
  enableTouch = true,
  className = '',
  height = '100%',
  theme = 'light',
  showTurnArtPlaceholder = false,
  onTurnCodeError,
  skillFilter = null,
  mode = 'illustrated',
}: BookReadingViewProps) {
  const isDark = theme === 'dark';
  const isTextOnly = mode === 'text-only';

  // Nav mode: expanded (full nav) or minimized (thin progress strip)
  const [navMode, setNavMode] = useState<'expanded' | 'minimized'>('expanded');
  const navHeight = isTextOnly
    ? (navMode === 'expanded' ? 56 : 28)
    : 0;

  // Callback ref backed by state so that when the container mounts,
  // useAutoPagination's effects re-run with the actual DOM element.
  const [readingAreaEl, setReadingAreaEl] = useState<HTMLDivElement | null>(null);
  const readingAreaRef = useCallback((node: HTMLDivElement | null) => {
    setReadingAreaEl(node);
  }, []);

  // Touch/swipe tracking
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  // Auto-paginate in text-only mode, accounting for nav bar height
  const virtualPages = useAutoPagination(pages, fontSize, lineSpacing, readingAreaEl, mode, navHeight);
  const displayPages = isTextOnly ? virtualPages : pages;
  const totalPages = displayPages.length;

  // Navigation handlers
  const goToPrevPage = useCallback(() => {
    if (currentPageIndex > 0) {
      onPageChange(currentPageIndex - 1);
    }
  }, [currentPageIndex, onPageChange]);

  const goToNextPage = useCallback(() => {
    if (currentPageIndex < totalPages - 1) {
      onPageChange(currentPageIndex + 1);
    }
  }, [currentPageIndex, totalPages, onPageChange]);

  // Clamp page index when virtual pages change (e.g. font size increase reduces page count)
  useEffect(() => {
    if (totalPages > 0 && currentPageIndex >= totalPages) {
      onPageChange(totalPages - 1);
    }
  }, [totalPages, currentPageIndex, onPageChange]);

  // Keyboard navigation
  useEffect(() => {
    if (!enableKeyboard) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevPage();
      if (e.key === 'ArrowRight') goToNextPage();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, goToPrevPage, goToNextPage]);

  // Touch/swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enableTouch) return;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  }, [enableTouch]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!enableTouch || !touchStartRef.current) return;
    
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    
    // Minimum swipe distance and max time
    const minSwipeDistance = 50;
    const maxSwipeTime = 300;
    
    // Only count horizontal swipes
    if (Math.abs(deltaX) > Math.abs(deltaY) && 
        Math.abs(deltaX) > minSwipeDistance && 
        deltaTime < maxSwipeTime) {
      if (deltaX > 0) {
        goToPrevPage(); // Swipe right = previous
      } else {
        goToNextPage(); // Swipe left = next
      }
    }
    
    touchStartRef.current = null;
  }, [enableTouch, goToPrevPage, goToNextPage]);

  // Tap zones for navigation
  const handleTap = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const relativeX = (e.clientX - rect.left) / rect.width;
    
    if (relativeX < 0.25) {
      goToPrevPage(); // Left quarter
    } else if (relativeX > 0.75) {
      goToNextPage(); // Right quarter
    }
  }, [goToPrevPage, goToNextPage]);

  // Clamp index synchronously to prevent blank page flash when virtualPages
  // shrinks (e.g. font size increase) but parent state hasn't updated yet.
  const safePageIndex = totalPages > 0
    ? Math.min(currentPageIndex, totalPages - 1)
    : 0;

  const currentPage = displayPages[safePageIndex];
  if (!currentPage) return null;

  // Skill-based word filtering:
  //   null  → no filter, show all colors (backward compat / preview mode)
  //   []    → no skills assigned, strip all colors
  //   [ids] → filter to matching skill levels only
  const filteredWords = skillFilter === null
    ? currentPage.words
    : skillFilter.length === 0
    ? currentPage.words.map(word => ({ ...word, turnCode: null }))
    : currentPage.words.map(word => {
        if (!word.turnCode) return word;
        if (!word.skillId || !skillFilter.includes(word.skillId)) {
          return { ...word, turnCode: null };
        }
        return word;
      });

  // Progress percentage (use clamped index for correct visual)
  const progressPercent = totalPages > 1 
    ? (safePageIndex / (totalPages - 1)) * 100 
    : 100;

  // ============================================================================
  // Text-Only Mode Render
  // ============================================================================

  if (isTextOnly) {
    const isExpanded = navMode === 'expanded';

    return (
      <div 
        className={`relative flex flex-col ${className}`}
        style={{ height }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Main reading area - book paper background */}
        <div 
          ref={readingAreaRef}
          className="flex-1 relative cursor-pointer select-none overflow-hidden"
          onClick={handleTap}
          style={{
            background: 'linear-gradient(175deg, #faf6f0 0%, #f5efe6 40%, #f0e9dd 100%)',
          }}
        >
          {/* Subtle page edge shadows */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 80px rgba(0,0,0,0.04), inset 0 2px 4px rgba(0,0,0,0.02)',
            }}
          />
          
          {/* Decorative page curl hint (bottom-right) */}
          <div 
            className="absolute bottom-0 right-0 w-12 h-12 pointer-events-none opacity-[0.03]"
            style={{
              background: 'linear-gradient(135deg, transparent 50%, #000 50%)',
            }}
          />

          {/* Text content - vertically centered with dynamic bottom padding for nav */}
          <div
            className="absolute inset-0 flex items-center justify-center px-8 sm:px-16 overflow-y-auto"
            style={{ paddingTop: 48, paddingBottom: navHeight + 48 }}
          >
            <div className="w-full">
              <TurnColorSentence
                words={filteredWords}
                colorMappings={colorMappings}
                phase={phase}
                className="break-words text-gray-900"
                style={{
                  fontSize: `${fontSize}px`,
                  lineHeight: lineSpacing,
                }}
                onTurnCodeError={onTurnCodeError}
              />
            </div>
          </div>
        </div>

        {/* Navigation: always rendered, toggles between expanded and minimized */}
        {totalPages > 1 && (
          <div
            className={`flex-shrink-0 bg-[#f5efe6] border-t border-[#e8dfd3] transition-all duration-200 ${
              isExpanded ? 'px-4 py-2' : 'px-2 py-0.5'
            }`}
          >
            {isExpanded ? (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={goToPrevPage}
                  disabled={safePageIndex === 0}
                  className="p-2 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-[#e8dfd3] text-[#8b7355] hover:bg-[#ddd3c3]"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {showProgress && (
                  <div className="flex-1 max-w-48 h-2 rounded-full overflow-hidden bg-[#e8dfd3]">
                    <div
                      className="h-full transition-all duration-300 bg-[#8b7355]"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                )}

                <button
                  onClick={goToNextPage}
                  disabled={safePageIndex >= totalPages - 1}
                  className="p-2 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-[#e8dfd3] text-[#8b7355] hover:bg-[#ddd3c3]"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setNavMode('minimized')}
                  className="p-1 rounded-full text-[#8b7355]/60 hover:text-[#8b7355] transition-colors"
                  aria-label="Minimize navigation"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevPage}
                  disabled={safePageIndex === 0}
                  className="p-0.5 rounded text-[#8b7355] disabled:opacity-30 transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-[#e8dfd3]">
                  <div
                    className="h-full transition-all duration-300 bg-[#8b7355]"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={safePageIndex >= totalPages - 1}
                  className="p-0.5 rounded text-[#8b7355] disabled:opacity-30 transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setNavMode('expanded')}
                  className="p-0.5 rounded text-[#8b7355]/60 hover:text-[#8b7355] transition-colors"
                  aria-label="Expand navigation"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Animation styles */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.98); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease-out forwards;
          }
        `}</style>
      </div>
    );
  }

  // ============================================================================
  // Illustrated Mode Render (original behavior)
  // ============================================================================

  return (
    <div 
      className={`relative flex flex-col ${className}`}
      style={{ height }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main reading area */}
      <div 
        ref={readingAreaRef}
        className="flex-1 relative cursor-pointer select-none"
        onClick={handleTap}
      >
        {/* FULL VIEWPORT IMAGE - hero background */}
        {currentPage.turnArtUrl ? (
          <img 
            key={safePageIndex}
            src={currentPage.turnArtUrl}
            alt={`Illustration for page ${safePageIndex + 1}`}
            className="absolute inset-0 w-full h-full object-cover animate-fadeIn"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : showTurnArtPlaceholder ? (
          /* Animated placeholder while Turn Art is generating */
          <div className={`absolute inset-0 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100'}`}>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="relative">
                {/* Pulsing background circle */}
                <div className="absolute inset-0 -m-4 rounded-full bg-[#5E5CE6]/10 animate-pulse" />
                <Wand2 className="w-12 h-12 text-[#5E5CE6]/40" />
              </div>
              <p className="mt-4 text-sm font-medium text-white/30">
                Creating illustration...
              </p>
            </div>
            {/* Subtle shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
          </div>
        ) : (
          <div className={`absolute inset-0 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100'}`} />
        )}

        {/* TEXT OVERLAY: white box with 2pt gray border, tight horizontal margins */}
        <div className="absolute inset-x-0 bottom-0 px-2 py-4 md:px-3 md:py-6 landscape:px-2 landscape:py-3">
          <div
            className={`rounded-2xl px-4 py-4 md:px-6 md:py-5 border-2 border-gray-300 max-w-3xl landscape:max-w-5xl landscape:px-8 mx-auto overflow-visible min-h-[4rem]
              ${isDark ? 'bg-white' : 'bg-white/95 backdrop-blur-sm'}`}
          >
            <TurnColorSentence
              words={filteredWords}
              colorMappings={colorMappings}
              phase={phase}
              className={`break-words overflow-visible ${isDark ? 'text-gray-900' : ''}`}
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: lineSpacing,
              }}
              onTurnCodeError={onTurnCodeError}
            />
          </div>
        </div>
      </div>

      {/* Navigation controls */}
      {showNavigation && totalPages > 1 && (
        <div
          className={`flex-shrink-0 flex items-center justify-center gap-6 px-4 py-3 ${
            isDark ? 'bg-white/10 backdrop-blur-sm border-t border-white/10' : 'bg-white/95 backdrop-blur-sm border-t border-amber-100'
          }`}
        >
          <button
            onClick={goToPrevPage}
            disabled={safePageIndex === 0}
            className={`p-3 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark
                ? 'bg-white/10 text-white hover:bg-white/15'
                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            }`}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {showProgress && (
            <div className={`w-48 h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/20' : 'bg-amber-100'}`}>
              <div
                className={`h-full transition-all duration-300 ${isDark ? 'bg-[#0A84FF]' : 'bg-amber-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}

          <button
            onClick={goToNextPage}
            disabled={safePageIndex >= totalPages - 1}
            className={`p-3 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark
                ? 'bg-white/10 text-white hover:bg-white/15'
                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            }`}
            aria-label="Next page"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}

      {!showNavigation && showProgress && (
        <div className={`absolute bottom-0 left-0 right-0 h-1 ${isDark ? 'bg-white/20' : 'bg-gray-200/30'}`}>
          <div
            className={`h-full transition-all duration-300 ${isDark ? 'bg-[#0A84FF]' : 'bg-amber-500'}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}

export default BookReadingView;
